import { type UseMutationResult, useMutation } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useTranslation } from 'react-i18next';

import api from '@/api/axios';
import { useErrorHandling } from '@/hooks/useErrorHandling';
import { useAuditStore } from '@/store/auditStore';
import { useNotificationStore } from '@/store/notificationStore';
import { shouldRetryRequest } from '@/utils/shouldRetryRequest';

interface UpdateAuditCaseFileParams {
  auditCaseUuid: string;
  inputFile: string;
}

const updateAuditCaseFile = async ({
  auditCaseUuid,
  inputFile,
}: UpdateAuditCaseFileParams): Promise<void> => {
  await api.put(`/audit-cases/${auditCaseUuid}/audit-file`, {
    inputFile: `data:application/vnd.ifs.axpx-neo;base64,${inputFile}`,
  });
};

export const useUpdateAuditCaseFile = (): UseMutationResult<
  void,
  unknown,
  UpdateAuditCaseFileParams
> => {
  const handleErrors = useErrorHandling();
  const notify = useNotificationStore((state) => state.notify);
  const { t } = useTranslation();
  const incrementPendingActions = useAuditStore((state) => state.incrementPendingActions);
  const decrementPendingActions = useAuditStore((state) => state.decrementPendingActions);

  return useMutation({
    mutationFn: updateAuditCaseFile,
    onMutate: () => incrementPendingActions(),
    onError: (error: unknown) => {
      const notifySaveError = () =>
        notify({ message: t('errors.audit_file_save_error'), type: 'error' });

      // Network errors have no response — handle them before handleErrors re-throws
      if (error instanceof AxiosError && !error.response) {
        notifySaveError();
        return;
      }

      handleErrors(error, { on400: notifySaveError });
    },
    onSettled: () => decrementPendingActions(),
    retry(failureCount, error) {
      return shouldRetryRequest(failureCount, error);
    },
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 10000),
  });
};
